import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { hasRole, selectActiveSite, selectDenormalizedUser } from '@redux/authent/authent.selectors';
import { useCreateUserMutation, useUpdateUserMutation } from '@services/authent/user.api';
import { getFirstNameAndName } from '@utils/authent/users.utils';
import { createErrorMessage, haveSameUniqueElements } from '@utils/common.utils';
import { userSchemaValidation } from '@validations/user.schema';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from '@/types/authent/login.types';
import { RoleEnum } from '@/types/authent/roles.types';

import { useUsersManagementContext } from '../UsersManagement';
import ResetPassword from './ResetPassword';
import UserForm from './UserForm';
import UserGroups from './UserGroups';

type UserDialogProps = {
  isNewUser: boolean;
  name: string;
  groupsExists: boolean;
} & Omit<CockpitDialogProps, 'children'>;

function UserDialog({
  isNewUser,
  name,
  groupsExists,
  open,
  isLoading,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<UserDialogProps>) {
  return (
    <CockpitDialog
      open={open}
      isLoading={isLoading}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id={`components.usersManagement.users.popup.${isNewUser ? 'descriptionSaveNew' : 'descriptionSave'}`}
          values={{ name: name }}
        />
      </Text>
      {groupsExists && (
        <Text color="neutral.100" width="100%" whiteSpace="pre-line">
          <FormattedMessage id="components.usersManagement.users.popup.descriptionSaveGroup" />
        </Text>
      )}
    </CockpitDialog>
  );
}

const emptyUser: Partial<User> = {
  firstName: '',
  active: true,
  name: '',
  email: '',
  login: '',
  password: '',
  deactivationDate: null,
  groups: [],
};

export default function UserFormContainer() {
  const { formatMessage } = useIntl();

  const { editedItem, setEditedItem } = useUsersManagementContext();
  const user = useAppSelector((state) => selectDenormalizedUser(state, editedItem?.id ?? '')) ?? emptyUser;
  const activeSite = useAppSelector(selectActiveSite);
  const [open, setOpen] = useState(false);
  const [createUser, { isLoading: isLoadingCreate }] = useCreateUserMutation();
  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();
  const isNewUser = user.id === undefined;
  const validationSchema = useValidation(userSchemaValidation);
  const canAccessGroup = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_GROUP));

  const formik = useFormik<Partial<User>>({
    initialValues: user,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isNewUser) {
        createUser({
          ...values,
          deactivationDate: null,
          activeGroup: null,
          activeSite,
          sites: [activeSite],
        } as User)
          .unwrap()
          .then((user) => {
            setOpen(false);
            createToast(
              formatMessage(
                { id: 'components.usersManagement.users.message.add' },
                { name: `${user.firstName} ${user.name}` },
              ),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          })
          .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
      } else {
        updateUser(values as User)
          .unwrap()
          .then((user) => {
            setOpen(false);
            createToast(
              formatMessage(
                { id: 'components.usersManagement.users.message.update' },
                { name: `${user.firstName} ${user.name}` },
              ),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          })
          .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
      }
    },
  });

  const hasChanged = () => {
    return (
      formik.initialValues.name !== formik.values.name ||
      formik.initialValues.active !== formik.values.active ||
      formik.initialValues.firstName !== formik.values.firstName ||
      formik.initialValues.email !== formik.values.email ||
      formik.initialValues.deactivationDate !== formik.values.deactivationDate ||
      formik.initialValues.login !== formik.values.login
    );
  };

  return (
    <CockpitEditorLayout
      dialog={
        <UserDialog
          open={open}
          isLoading={isLoadingCreate || isLoadingUpdate}
          confirmLabel={formatMessage({ id: 'global.save' })}
          cancelLabel={formatMessage({ id: 'global.cancel' })}
          onConfirm={formik.submitForm}
          onCancel={() => setOpen(false)}
          isNewUser={isNewUser}
          name={`${formik.values.firstName} ${formik.values.name}`}
          groupsExists={formik.values.groups?.length === 0}
        />
      }
      breadcrumbs={
        <Breadcrumb
          items={[
            {
              label: formatMessage({ id: 'components.usersManagement.users.title' }),
              action: () => setEditedItem(null),
            },
            {
              label: isNewUser
                ? formatMessage({
                    id: 'components.usersManagement.users.newUser',
                  })
                : getFirstNameAndName(user as User),
            },
          ]}
        />
      }
      onBack={() => setEditedItem(null)}
      onSave={() => setOpen(true)}
      isSaving={isLoadingCreate || isLoadingUpdate}
      isSaveDisabled={!formik.isValid || (!hasChanged() && haveSameUniqueElements(formik.values.groups!, user.groups!))}
    >
      <Tabs.Root
        orientation="vertical"
        defaultValue="userParams"
        size="md"
        lazyMount
        variant="vertical"
        userSelect="none"
      >
        <Tabs.List>
          <Tabs.Trigger value="userParams">
            <HStack width="100%">
              <FormattedMessage id="components.usersManagement.users.userParams.title" />
              {hasChanged() && <ModifiedTag />}
            </HStack>
          </Tabs.Trigger>
          {(canAccessGroup || !isNewUser) && (
            <Tabs.Trigger value="groups">
              <HStack width="100%">
                <FormattedMessage id="components.usersManagement.users.groups.title" />
                {!haveSameUniqueElements(formik.values.groups!, user.groups!) && <ModifiedTag />}
              </HStack>
            </Tabs.Trigger>
          )}
          {!isNewUser && (
            <Tabs.Trigger value="password">
              <FormattedMessage id="components.usersManagement.users.password.title" />
            </Tabs.Trigger>
          )}
        </Tabs.List>
        <Tabs.ContentGroup backgroundColor="neutral.700">
          <Tabs.Content value="userParams">
            <UserForm formik={formik} />
          </Tabs.Content>
          {(canAccessGroup || !isNewUser) && (
            <Tabs.Content value="groups">
              <UserGroups formik={formik} activeGroup={user.activeGroup} />
            </Tabs.Content>
          )}
          {!isNewUser && (
            <Tabs.Content value="password">
              <ResetPassword id={user.id!} />
            </Tabs.Content>
          )}
        </Tabs.ContentGroup>
      </Tabs.Root>
    </CockpitEditorLayout>
  );
}
